import { CheckboxField, TextField } from '@aws-amplify/ui-react';
import React, { useCallback, useEffect, useState } from 'react';
import { MdDragIndicator } from 'react-icons/md';
import type { EnrichedFieldMappingDto } from '../generated/api';
import './FieldMappingsEditor.scss';
import { INSTITUTION_FIELD_KEYS, InstitutionFieldsSlideout } from './InstitutionFieldsSlideout';
import { NotMappedWarning } from './NotMappedWarning';
import { TitleFieldsSlideout } from './TitleFieldsSlideout';

export type ExportTarget = 'CSV' | 'JSON' | 'HUBSPOT' | 'SALESFORCE';

// Fields that should open the title fields explainer when clicked
const TITLE_FIELD_KEYS = ['titleModifier', 'titleArea', 'titleRole'];

export interface FieldMappingsEditorProps {
  mappings: EnrichedFieldMappingDto[];
  onChange: (mappings: EnrichedFieldMappingDto[]) => void;
  disabled?: boolean;
  target?: ExportTarget;
  /** Callback to report whether any visible CRM fields are left unmapped */
  onValidationChange?: (hasErrors: boolean) => void;
}

const getTargetLabel = (target?: ExportTarget): string => {
  switch (target) {
    case 'HUBSPOT':
      return 'HubSpot Field';
    case 'SALESFORCE':
      return 'Salesforce Field';
    case 'CSV':
      return 'CSV Column Name';
    case 'JSON':
      return 'JSON Property Name';
    default:
      return 'Export As';
  }
};

export const FieldMappingsEditor: React.FC<FieldMappingsEditorProps> = ({
  mappings,
  onChange,
  disabled = false,
  target,
  onValidationChange
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [titleSlideoutOpen, setTitleSlideoutOpen] = useState(false);
  const [institutionSlideoutOpen, setInstitutionSlideoutOpen] = useState(false);
  const [institutionSlideoutField, setInstitutionSlideoutField] = useState<string | null>(null);

  // Check if this is a CRM target (field mappings are read-only, managed in Settings)
  const isCrmTarget = target === 'HUBSPOT' || target === 'SALESFORCE';

  // For CRM targets, check if any visible fields are unmapped
  const hasUnmappedCrmFields = isCrmTarget && mappings.some(
    m => m.visible && !(m.target as unknown as string)?.trim()
  );

  // Notify parent of validation errors (unmapped CRM fields)
  useEffect(() => {
    onValidationChange?.(hasUnmappedCrmFields);
  }, [hasUnmappedCrmFields, onValidationChange]);

  const handleDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      if (disabled) return;
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());

      // Clone the row to use as drag image to avoid Chrome's white background
      const row = e.currentTarget as HTMLElement;
      const clone = row.cloneNode(true) as HTMLElement;
      clone.style.cssText = `
      position: absolute;
      left: -9999px;
      width: ${row.offsetWidth}px;
      background: #302f3d;
      border: none;
    `;
      // Also fix input backgrounds inside the clone
      clone.querySelectorAll('input').forEach((input) => {
        (input as HTMLElement).style.background = '#302f3d';
      });
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, 20, 20);

      // Clean up after browser captures the image
      requestAnimationFrame(() => {
        document.body.removeChild(clone);
      });
    },
    [disabled]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (disabled || draggedIndex === null) return;
      setDragOverIndex(index);
    },
    [disabled, draggedIndex]
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (disabled || draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }

      const newMappings = [...mappings];
      const [draggedItem] = newMappings.splice(draggedIndex, 1);
      newMappings.splice(dropIndex, 0, draggedItem);

      // Update order values
      const reorderedMappings = newMappings.map((mapping, idx) => ({
        ...mapping,
        order: idx
      }));

      onChange(reorderedMappings);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [disabled, draggedIndex, mappings, onChange]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleVisibleChange = useCallback(
    (index: number, visible: boolean) => {
      const newMappings = mappings.map((mapping, idx) => (idx === index ? { ...mapping, visible } : mapping));
      onChange(newMappings);
    },
    [mappings, onChange]
  );

  const handleTargetChange = useCallback(
    (index: number, target: string) => {
      const newMappings = mappings.map((mapping, idx) =>
        idx === index ? { ...mapping, target: target as unknown as EnrichedFieldMappingDto['target'] } : mapping
      );
      onChange(newMappings);
    },
    [mappings, onChange]
  );

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      const newMappings = mappings.map((mapping) => ({
        ...mapping,
        // Only toggle non-required fields
        visible: mapping.required ? true : checked
      }));
      onChange(newMappings);
    },
    [mappings, onChange]
  );

  const sortedMappings = [...mappings].sort((a, b) => a.order - b.order);

  // Calculate toggle all state: checked if all non-required fields are visible
  const nonRequiredMappings = mappings.filter((m) => !m.required);
  const allChecked = nonRequiredMappings.length > 0 && nonRequiredMappings.every((m) => m.visible);
  const someChecked = nonRequiredMappings.some((m) => m.visible) && !allChecked;

  return (
    <div className={`field-mappings-editor ${disabled ? 'field-mappings-editor--disabled' : ''}`}>
      <div className="field-mappings-editor__header">
        <div className="field-mappings-editor__header-drag"></div>
        <div className="field-mappings-editor__header-visible">
          <CheckboxField
            label="Include"
            labelHidden
            checked={allChecked}
            onChange={(e) => handleToggleAll(e.target.checked)}
            isDisabled={disabled}
            name="toggle-all-visible"
            size="large"
            className={someChecked ? 'indeterminate' : ''}
          />
        </div>
        <div className="field-mappings-editor__header-source">Field</div>
        <div className="field-mappings-editor__header-target">{getTargetLabel(target)}</div>
        <div className="field-mappings-editor__header-example">Example Value</div>
      </div>
      <div className="field-mappings-editor__list">
        {sortedMappings.map((mapping, index) => {
          const isDragging = draggedIndex === index;
          const isDragOver = dragOverIndex === index && draggedIndex !== index;

          return (
            <div
              key={mapping.source}
              className={`field-mappings-editor__row ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${!mapping.visible ? 'hidden-field' : ''}`}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className="field-mappings-editor__cell field-mappings-editor__cell--drag">
                {!disabled && <MdDragIndicator className="field-mappings-editor__drag-handle" />}
              </div>
              <div className="field-mappings-editor__cell field-mappings-editor__cell--visible">
                <span
                  data-tooltip-id={mapping.required ? 'main-tooltip' : undefined}
                  data-tooltip-content={mapping.required ? 'This field is required and cannot be excluded' : undefined}
                >
                  <CheckboxField
                    label=""
                    labelHidden
                    checked={mapping.visible}
                    onChange={(e) => handleVisibleChange(index, e.target.checked)}
                    isDisabled={disabled || mapping.required}
                    name={`visible-${mapping.source}`}
                    size="large"
                  />
                </span>
              </div>
              <div className="field-mappings-editor__cell field-mappings-editor__cell--source">
                <span className="field-mappings-editor__label">{mapping.label}</span>
                {mapping.datatype && <span className="desc-12">{mapping.datatype}</span>}
              </div>
              <div className="field-mappings-editor__cell field-mappings-editor__cell--target">
                {isCrmTarget ? (
                  <span className="field-mappings-editor__target-static mono-1">
                    {(mapping.target as unknown as string) || (
                      mapping.visible && (
                        <NotMappedWarning />
                      )
                    )}
                  </span>
                ) : (
                  <TextField
                    label=""
                    labelHidden
                    value={(mapping.target as unknown as string) ?? ''}
                    onChange={(e) => handleTargetChange(index, e.target.value)}
                    isDisabled={disabled || !mapping.visible}
                    hasError={mapping.visible && !(mapping.target as unknown as string)?.trim()}
                    size="small"
                    className="field-mappings-editor__target-input"
                  />
                )}
              </div>
              <div className="field-mappings-editor__cell field-mappings-editor__cell--example">
                {TITLE_FIELD_KEYS.includes(mapping.source) ? (
                  <span
                    className="field-mappings-editor__example field-mappings-editor__example--clickable"
                    onClick={() => setTitleSlideoutOpen(true)}
                  >
                    {mapping.example}
                  </span>
                ) : INSTITUTION_FIELD_KEYS.includes(mapping.source) ? (
                  <span
                    className="field-mappings-editor__example field-mappings-editor__example--clickable"
                    onClick={() => {
                      setInstitutionSlideoutField(mapping.source);
                      setInstitutionSlideoutOpen(true);
                    }}
                  >
                    {mapping.example}
                  </span>
                ) : (
                  <span className="field-mappings-editor__example">{mapping.example}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <TitleFieldsSlideout isOpen={titleSlideoutOpen} onClose={() => setTitleSlideoutOpen(false)} />
      <InstitutionFieldsSlideout
        isOpen={institutionSlideoutOpen}
        onClose={() => setInstitutionSlideoutOpen(false)}
        fieldKey={institutionSlideoutField}
      />
    </div>
  );
};
